import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, FileText, Mail, LogOut, Menu, X, LayoutDashboard, Home as HomeIcon } from 'lucide-react';
import { getAnalyticsSummary } from '../lib/api';

export default function OpsLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const role = localStorage.getItem('admin_role') || 'admin';
  const name = localStorage.getItem('admin_name') || localStorage.getItem('admin_email') || '';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ newLeads: 0 });

  // Close mobile nav on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Fetch notification badge counts
  useEffect(() => {
    getAnalyticsSummary()
      .then(r => {
        const hot = r.data?.hot_leads || 0;
        const total = r.data?.total_leads || 0;
        setBadges({ newLeads: total, hotLeads: hot });
      })
      .catch(() => {});
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_name');
    navigate('/ops/login');
  };

  const navItems = [
    { path: '/ops/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/ops/leads', label: 'Lead CRM', icon: Users, badge: badges.newLeads || null },
    ...(role === 'admin' ? [{ path: '/ops/guides', label: 'Content CMS', icon: FileText }] : []),
    { path: '/ops/drip', label: 'Drip Automation', icon: Mail },
    { path: '/ops/ddf', label: 'MLS Listings', icon: HomeIcon },
  ];

  const sidebar = (
    <>
      <div className="p-5 border-b border-white/5">
        <Link to="/ops/dashboard" className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-white text-slate-900 flex items-center justify-center text-[10px] font-bold">B</div>
          <div>
            <p className="text-sm font-semibold text-white">Buy In Burlington</p>
            <p className="text-[10px] text-white/30">Operations</p>
          </div>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <div className="space-y-1">
          {navItems.map(item => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-2.5">
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </div>
                {item.badge > 0 && (
                  <span className="bg-emerald-500 text-white text-[10px] font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1.5">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs text-white/60 truncate">{name}</p>
            <p className="text-[10px] text-white/25 capitalize">{role}</p>
          </div>
          <button onClick={handleLogout} className="p-2 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors" data-testid="ops-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 bg-slate-950 flex-col flex-shrink-0 sticky top-0 h-screen">
        {sidebar}
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-slate-950 h-14 flex items-center justify-between px-4">
        <Link to="/ops/dashboard" className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-white text-slate-900 flex items-center justify-center text-[9px] font-bold">B</div>
          <span className="text-sm font-semibold text-white">Ops</span>
        </Link>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-white/60">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="md:hidden fixed top-0 left-0 bottom-0 w-64 bg-slate-950 z-50 flex flex-col animate-slide-right">
            {sidebar}
          </aside>
        </>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 md:mt-0 mt-14">
        {children}
      </main>
    </div>
  );
}
